import React, { useCallback } from 'react';
import clsx from 'clsx';
import ReactTooltip from 'react-tooltip';
import Kbd from 'react-scale/components/design-system/Kbd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { makeStyles } from 'react-scale/components/design-system/styles';
import { isChapterWorkflowStep } from '@scale/shared/taskerWorkflow/TaskerWorkflow';
import { WorkflowHook } from 'react-scale/components/scalers/Sidebar/GuidedWorkflowPanel/useWorkflow';

interface ToolTipButtonProps {
  workflowHook: WorkflowHook;
}

const useStyles = makeStyles({
  iconToggle: {
    cursor: 'pointer',
    fontSize: '16px',
    padding: '0px 6px',
    color: 'var(--color-RemoGray50)',
    '&.isDisabled': {
      color: 'var(--color-RemoGray20)',
    },
  },
});

export const BackButton: React.FC<ToolTipButtonProps> = props => {
  const classes = useStyles();
  const { movePreviousSiblingStep } = props.workflowHook;

  const handleClickBackward: React.MouseEventHandler = useCallback(
    e => {
      e.stopPropagation();
      movePreviousSiblingStep();
    },
    [movePreviousSiblingStep],
  );

  return (
    <div
      data-tip
      data-for="backwardButton"
      data-effect="solid"
      data-place="bottom"
      className={classes.iconToggle}
    >
      <ReactTooltip id="backwardButton">
        {
          <div>
            Previous Step <Kbd>SHIFT</Kbd> + <Kbd>B</Kbd>
          </div>
        }
      </ReactTooltip>
      <div onClick={handleClickBackward}>
        <FontAwesomeIcon icon="backward" />
      </div>
    </div>
  );
};

export const ForwardButton: React.FC<ToolTipButtonProps> = props => {
  const classes = useStyles();
  const { moveNextSiblingStep } = props.workflowHook;

  const handleClickForward: React.MouseEventHandler = useCallback(
    e => {
      e.stopPropagation();
      moveNextSiblingStep();
    },
    [moveNextSiblingStep],
  );

  return (
    <div
      data-tip
      data-for="forwardButton"
      data-effect="solid"
      data-place="bottom"
      className={classes.iconToggle}
    >
      <ReactTooltip id="forwardButton">
        {
          <div>
            Next Step <Kbd>SHIFT</Kbd> + <Kbd>N</Kbd>
          </div>
        }
      </ReactTooltip>
      <div className={classes.iconToggle} onClick={handleClickForward}>
        <FontAwesomeIcon icon="forward" />
      </div>
    </div>
  );
};

export const FastBackButton: React.FC<ToolTipButtonProps> = props => {
  const classes = useStyles();
  const { setActiveStepId, workflowState } = props.workflowHook;

  const { currentStep } = workflowState;
  const parentId = currentStep.id.slice(0, currentStep.id.length - (currentStep.name.length + 1));

  const handleClickFastBackward: React.MouseEventHandler = useCallback(
    e => {
      e.stopPropagation();

      if (isChapterWorkflowStep(currentStep)) {
        // If it's the chapter node, do nothing.
      } else {
        setActiveStepId(parentId);
      }
    },
    [parentId, setActiveStepId, currentStep],
  );

  const isDisabled = currentStep.depth <= 1;

  return (
    <div
      data-tip
      data-for="fastBackwardButton"
      data-effect="solid"
      data-place="bottom"
      className={clsx(classes.iconToggle, { isDisabled })}
    >
      <ReactTooltip id="fastBackwardButton">{<div>Back to Parent</div>}</ReactTooltip>
      <div onClick={handleClickFastBackward}>
        <FontAwesomeIcon icon="fast-backward" />
      </div>
    </div>
  );
};
